import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isArray } from 'lodash'
import classNames from 'classnames'
import 'react-step-progress-bar/styles.css'

import './styles.scss'

import { getCart, updateInCart } from '../../store/modules/cart/actions'
import ReassuranceSlider from '../../../shared/components/atoms/ReassuranceSlider'
import CartItem from '../../../shared/components/molecules/CartItem'
import { isMobile } from '../../../shared/helpers/babHelpers'
import { ping } from '../../../shared/helpers/ping'
import {
  calculateThresholds,
  isSettingsMissing,
  removeAllGifts,
  updateMiniCartCount,
  getCartType
} from '../../utils/helpers'
import {
  newchargeCheckoutUrl,
  isNewchargeCheckoutRedirectionActive,
  buildNewchargeCheckoutLoadedUrl
} from '../../utils/newcharge_helpers'
import ProductList from '../../components/molecules/ProductList'
import ProgressBar from '../../components/molecules/ProgressBar'
import GiftList from '../../components/molecules/GiftList'
import Sample from '../../components/atoms/Sample'
import { windowAmplitudeTrack } from '../../../shared/helpers/utils'

export default function SlideCart ({
  products,
  freeGiftsSelectionAvailable,
  secondStepGiftsAvailable,
  samplesSettingTypesSkus,
  availableSamples
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const cart = useSelector(state => state.cart)
  const [chosenSample, setChosenSample] = useState([])
  const [chosenGift, setChosenGift] = useState([])
  const sampleRef = useRef()
  const giftRef = useRef()
  const [samples, setSamples] = useState([])
  const [gifts, setGifts] = useState([])
  const [giftsList, setGiftsList] = useState([])
  const [isSelectionVisible, setIsSelectionVisible] = useState(false)

  const shippingThresholds = document.querySelector('meta[name="shipping-reduction-threshold"]').content.split(';')
  const samplesSettings = document.querySelector('meta[name="samples"]').content
  const hiddenProducts = document.querySelector('meta[name="hidden-products"]').content.split(',')

  const thresholds = calculateThresholds(cart, shippingThresholds, freeGiftsSelectionAvailable, secondStepGiftsAvailable)

  const giftSkus = document.querySelector('meta[name="gifts"]')?.content?.split(';\n')
  const specialProducts = giftSkus.find(skus => skus.includes('SPECIALS'))?.split('|')[1].split(',')
  const subscriptionProducts = giftSkus.find(sku => sku.includes('BUNDLE'))?.split('|')[1].split(',')

  const hasSubscription = cart?.items?.some(item => item.sku.includes('BUNDLE.C'))
  const bundleDiscountedGiftAvailable = hasSubscription && products.find(product => product.sku === subscriptionProducts[0])
  const hasSpecial = cart?.items?.some(item => item.upsell_categories?.includes('special'))
  /* global Shopify */
  const specialGiftAvailable = hasSpecial && Shopify.locale === 'fr' && !hasSubscription && products.find(product => product.sku === specialProducts[0])
  const giftsAvailable = bundleDiscountedGiftAvailable || specialGiftAvailable || freeGiftsSelectionAvailable

  const cartType = cart.items?.length > 0 && getCartType(cart.items)
  const availableCartItem = cart.items?.some(item => products.find(product => product.sku === item.sku))
  const welcomeKit = cart.items?.find(product => product.sku.startsWith('TRAV.') && product.product_type === 'freebie')

  useEffect(() => {
    isSettingsMissing()

    if (!cart || isArray(cart)) {
      dispatch(getCart())
    }
  }, [])

  useEffect(() => {
    if (hasSubscription || !freeGiftsSelectionAvailable) {
      removeAllGifts(cart?.items, dispatch, updateInCart).then(r => {
        dispatch(getCart())
      })
    }

    const subscriptionProduct = cart?.items?.find(item => subscriptionProducts[0].includes(item.sku))
    if (!hasSubscription && subscriptionProduct) {
      dispatch(updateInCart(subscriptionProduct, 0))
    }
  }, [hasSubscription])

  useEffect(() => {
    if (hasSubscription && !cart?.items?.find(item => subscriptionProducts[0].includes(item.sku))) {
      setIsSelectionVisible(true)
    }
  }, [products])

  useEffect(() => {
    if (cart?.items?.length === 1 && chosenSample?.length) {
      dispatch(updateInCart(chosenSample, 0))
      setChosenSample([])
    }

    const cartContainsOnlyGifts = cart?.items?.every(item => item.product_type === 'freebie')
    if (cartContainsOnlyGifts) {
      setChosenGift([])

      removeAllGifts(cart?.items, dispatch, updateInCart).then(r => r)
    }

    let toRemove = cart?.items?.filter(item => item.product_type === 'freebie') || []
    if (toRemove?.length > 1) {
      toRemove = products.filter(item => toRemove.map(p => p.sku)
        .includes(item.sku) && item.product_category === 'gift')
    }
    if (toRemove.length > 2) {
      setChosenGift([])
      dispatch(updateInCart(toRemove[0], 0))
    }

    if (document.getElementById('gift-anchor')) {
      document.getElementById('gift-anchor').onclick = function () {
        giftRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [cart?.items?.length])

  const track = () => {
    windowAmplitudeTrack('IncentiveCartSubmitted')
  }

  /**
   * every time cart total_price is updated we will look if customer can still keep
   * one, two or zero gift
   * @property {String} product_type
   */
  useEffect(() => {
    let toRemove = cart?.items?.filter(item => item.product_type === 'freebie')
    if (toRemove?.length > 1) {
      toRemove = products.filter(item => toRemove.map(p => p.sku).includes(item.sku) && item.product_category === 'gift')
    }

    if (cart.total_price === 0) {
      removeAllGifts(cart?.items, dispatch, updateInCart).then(r => r)
      dispatch(updateInCart(chosenSample, 0))
    }

    const { freeProductsThreshold, secondFreeProductsThreshold } = thresholds

    // remove second step gift
    if (cart.total_price >= freeProductsThreshold && cart.total_price < secondFreeProductsThreshold) {
      if (toRemove?.length > 1) dispatch(updateInCart(toRemove[0], 0))
    }

    if (thresholds.priceDifferenceGift > 0 && toRemove?.length) {
      dispatch(updateInCart(toRemove[0], 0))
      setChosenGift([])
    }
  }, [cart?.total_price])

  /**
   * get current gifts from cart (if customer has already selected gifts)
   * @type {Array}
   */
  const actualFreebieProducts = cart?.items?.reduce((acc, item) => {
    if (item.product_type === 'freebie') acc.push(item)
    return acc
  }, [])

  useEffect(() => {
    const incentiveCartSlide = document.getElementById('incentive-cart-slide')
    if (incentiveCartSlide.dataset.pingedFirstOpened) return

    if (incentiveCartSlide.getAttribute('style') === 'display: block;') {
      incentiveCartSlide.dataset.pingedFirstOpened = true
      ping('slide_cart_first_opened')
    }
  })

  const closeCart = () => {
    document.getElementById('incentive-cart-slide').style.display = 'none'
    if (document.getElementById('chat-button')) document.getElementById('chat-button').style.display = 'block'
    if (document.getElementById('launcher')) document.getElementById('launcher').style.display = 'block'
    updateMiniCartCount(cart)
  }

  const pushDatalayer = () => {
    const dataLayerProducts = cart.items.map(item => {
      if (item.final_price > 0) {
        return ({
          name: item.title,
          id: item.sku,
          price: item.final_price / 100,
          brand: 'Joone',
          category: item.product_type,
          variant: item.variant_title,
          quantity: item.quantity
        })
      }
    }).filter(p => p)

    const dataLayerSubscription = cart.items.map(item => {
      if (item.product_type === 'abonnement') {
        return ({
          name: item.title,
          id: item.sku,
          price: item.final_price / 100,
          brand: 'Joone',
          category: item.product_type,
          variant: item.variant_title,
          quantity: item.quantity
        })
      }
    })

    const dataLayerOneShot = cart.items.map(item => {
      if (item.product_type !== 'abonnement' && item.final_price > 0) {
        return ({
          name: item.title,
          id: item.sku,
          price: item.final_price / 100,
          brand: 'Joone',
          category: item.product_type,
          variant: item.variant_title,
          quantity: item.quantity
        })
      }
    })

    dataLayer.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: { step: '1', option: 'cart' }, // TODO modifier le step si le panier est supérieur à 79.90 € ??
          products: dataLayerProducts,
          subscription: dataLayerSubscription,
          oneShot: dataLayerOneShot
        }
      }
    })
  }

  const productsWithoutProgressBar = document.querySelector('meta[name="incentive_cart_hidden_shipping_sample_gift"]').content.split(',')
  const removeFromCart = cart.items?.length > 0 && cart.items.every(item => productsWithoutProgressBar.includes(item.sku))

  const welcomeKitAndSubscription = welcomeKit && hasSubscription && window.localStorage.getItem('welcomeKitContext')
  const totalPrice = welcomeKitAndSubscription ? Number((cart.total_price - welcomeKit.price) / 100).toFixed(2) : Number(cart.total_price / 100).toFixed(2)

  const redirectToNewchargeCheckout = async (event) => {
    if (!isNewchargeCheckoutRedirectionActive()) return

    if (newchargeCheckoutUrl() === '') {
      alert(t('slideCart:newcharge_checkout_url_missing_error'))
      return
    }

    try {
      const response = await fetch('/cart.js')
      const cartJsData = await response.json()
      const newchargeCheckoutLoadedUrl = buildNewchargeCheckoutLoadedUrl(
        newchargeCheckoutUrl(),
        cartJsData
      )

      window.location.href = newchargeCheckoutLoadedUrl
    } catch (error) {
      console.log('error:', error)
      alert(t('slideCart:newcharge_checkout_redirection_error'))
    }
  }

  return (
    <form action='/cart' method='post' id='cart_form'>
      <div id='incentive-cart-slide' className='slide-cart-overlay' onClick={closeCart}>
        <div className='incentive-cart-slide' onClick={e => e.stopPropagation()}>
          <div className='slide-cart-header'>
            <div onClick={closeCart}>
              {isMobile()
                ? <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/fleche.svg?v=1622731484' />
                : <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/fleche_black.svg?v=1622794263' />}
            </div>
            <div className='slide-cart-title'>{t('slideCart:title')}</div>
          </div>

          <ReassuranceSlider
            contents={Object.entries(t(`slideCart:${cart.items?.find(item => item.sku.includes('BUNDLE'))
              ? 'reassurance'
              : 'reassuranceOneOff'}`,
            { returnObjects: true })).map(([key, value]) => value)}
          />

          <div ref={giftRef} />
          {!removeFromCart &&
            cart?.items &&
            thresholds.priceDifferenceGift <= 0 &&
            giftsAvailable &&
            availableCartItem &&
            <GiftList
              secondStepGiftsAvailable={secondStepGiftsAvailable}
              actualFreebieProducts={actualFreebieProducts}
              chosenGift={chosenGift}
              setChosenGift={setChosenGift}
              gifts={gifts}
              setGifts={setGifts}
              giftsList={giftsList}
              setGiftsList={setGiftsList}
              isSelectionVisible={isSelectionVisible}
              setIsSelectionVisible={setIsSelectionVisible}
            />}

          <div className='slide-cart-content'>
            {(!cart || cart.items?.length === 0)
              ? <div className='no-content'>
                <div className='no-content-text'>{t('slideCart:noContent')}</div>
                <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/panier-vide-250px.gif?v=1639675500' className='no-content-img' />
              </div>
              : cart.items?.map((item, i) => {
                if (hiddenProducts.includes(item.sku)) return
                if (samples && samples.includes(item.sku)) return

                return (
                  <CartItem
                    updateInCart={updateInCart}
                    item={item}
                    key={i}
                    isGift={chosenGift && gifts?.includes(item.sku)}
                    setIsSelectionVisible={setIsSelectionVisible}
                  />
                )
              })}

            <div ref={sampleRef}>
              {!removeFromCart &&
              samplesSettings &&
                <ProductList
                  actualFreebieProducts={actualFreebieProducts}
                  chosenProduct={chosenSample}
                  setChosenProduct={setChosenSample}
                  products={samples}
                  setProducts={setSamples}
                  TypeComponent={Sample}
                  cart={cart}
                  type='sample'
                />}
            </div>
          </div>

          <div className='slide-cart-recap'>
            <div className='slide-cart-price'>
              <div>
                <p>{t('slideCart:total')}</p>
                <p>{t('general:dynamicPrice', { price: totalPrice })}</p>
              </div>

              {!removeFromCart && <div>
                {cart.total_price >= thresholds.currentShippingThreshold &&
                  <>
                    <p>{t('general:delivery')}</p>
                    <p>{t('general:delivery_price')}</p>
                  </>}
              </div>}
            </div>

            {cart?.items &&
            !cart.items.some(item => item.sku.includes('BUNDLE')) &&
            shippingThresholds &&
              <ProgressBar
                freeGiftsSelectionAvailable={freeGiftsSelectionAvailable}
                secondStepGiftsAvailable={secondStepGiftsAvailable}
                percentage={thresholds.percentage}
                freeProductsThreshold={thresholds.freeProductsThreshold}
                secondFreeProductsThreshold={thresholds.secondFreeProductsThreshold}
                currentShippingThreshold={thresholds.currentShippingThreshold}
                progressBarMax={thresholds.progressBarMax}
                priceDifferenceGift={thresholds.priceDifferenceGift}
                priceDifferenceShipping={thresholds.priceDifferenceShipping}
                giftsList={giftsList}
                samplesSettingTypesSkus={samplesSettingTypesSkus}
                availableSamples={availableSamples}
                cartType={cartType}
                availableCartItem={availableCartItem}
              />}

            <div className={classNames('cart-ctas', { oneCTA: cart.items?.length === 0 })}>
              <a href='/' className={classNames({ mainCTA: cart.items?.length === 0 })}>
                {t('slideCart:continueShopping')}
              </a>

              {cart.items?.length > 0 &&
                <input
                  type={isNewchargeCheckoutRedirectionActive() ? 'button' : 'submit'}
                  className='add_to_cart js-cart-submit mainCTA'
                  id='checkout'
                  name='checkout'
                  value={isNewchargeCheckoutRedirectionActive() ? t('slideCart:newcharge_order') : t('slideCart:order')}
                  onClick={(e) => {
                    track()
                    pushDatalayer()
                    redirectToNewchargeCheckout()
                  }}
                />}
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}
